/* eslint-disable @typescript-eslint/no-shadow */
import './ChatInputBox.scss';

import React, {
  lazy,
  memo,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Avatar,
  Divider,
  message,
  Space,
  Spin,
  Typography,
} from 'antd';
import { BsEmojiSmile } from 'react-icons/bs';
import {
  Mention,
  MentionsInput,
  MentionsInputProps,
  SuggestionDataItem,
} from 'react-mentions';
import { BottomSheet } from 'react-spring-bottom-sheet';
import TMIcon from 'tm-icons-library';

import { LoadingOutlined } from '@ant-design/icons';

import { useTheme } from '../../context/ThemeProvider';
import {
  COMMUNITY_MAX_INPUT_MEDIA_FILE_SIZE,
} from '../../screens/TQConnect/constants';
import { getChatFileType } from '../../shared/communityUtils';
import { MEDIA_CONTENT } from '../../shared/constants';
import showAppError from '../../shared/error';
import {
  SingleFileType,
  uploadFiles,
  uploadFilesHandler,
} from '../../shared/file';
import { extractTextAndMentionsMap } from '../../shared/utils';
import { IMentionedUser } from '../../types/commentTypes';
import {
  CHAT_MEDIAS,
  ChatMediaType,
} from '../../types/communityMessagesTypes';
import BottomSheetTile from '../BottomSheetTile/BottomSheetTile';
import ChatInputFilePreview from './ChatInputFilePreview';

const EmojiPickerModel = lazy(
  () => import('../EmojiPickerModel/EmojiPickerModel'),
);

const ICON_SIZE = 20;

const MAX_FILES_LIMIT = 10;

const filesDividerStyle: React.CSSProperties = {
  marginInline: 8,
  height: 60,
  borderColor: 'var(--border200)',
};

interface Props {
  messageText?: string;
  onSend: (text: string, files?: File[], onSuccess?: () => void) => void;
  isSending: boolean;
  placeholder?: string;
  hideUpload?: boolean;
  accept?: ChatMediaType[];
  hidden?: boolean;
  searchMentionedUser?: (searchText: string) => Promise<IMentionedUser[]>;
  onAddPoll?: (text?: string) => void;
  containerElement?: Element | null;
  isFooterActionsVisible?: boolean;
}

const ChatInputBox: React.FC<Props> = ({
  messageText = '',
  onSend,
  isSending,
  placeholder = 'Type a message...',
  hideUpload = false,
  accept = CHAT_MEDIAS,
  hidden,
  searchMentionedUser,
  onAddPoll,
  containerElement,
  isFooterActionsVisible,
}) => {
  const { colors: COLORS } = useTheme();

  const [mentionsDropdownPosition, setMentionsDropdownPosition] = useState<
    'top' | 'bottom'
  >('bottom');

  const [text, setText] = useState('');
  const [mentionsMap, setMentionsMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [files, setFiles] = useState<
    {
      type: ChatMediaType;
      file: File;
      contentUrl: string;
    }[]
  >([]);
  const [emptySearchResults, setEmptySearchResults] = useState<string[]>([]);
  const [isFilesDropdownOpen, setIsFilesDropdownOpen] = useState(false);
  const [isEmojiSheetOpen, setIsEmojiSheetOpen] = useState(false);

  const mentionsRef = useRef<React.Component<MentionsInputProps>>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionContainerRef = useRef<HTMLDivElement>(null);
  const filesContainerRef = useRef<HTMLDivElement>(null);
  const emojiCursorRef = useRef({ start: 0, end: 0 });

  // separate out files based on type to reorganize them in the UI (order: video, image, audio, document)
  const state = useMemo(() => {
    const videoFiles = files.filter((f) => f.type === 'video');
    const imageFiles = files.filter((f) => f.type === 'image');
    const audioFiles = files.filter((f) => f.type === 'audio');
    const documentFiles = files.filter((f) => f.type === 'doc');

    return {
      videoFiles,
      imageFiles,
      audioFiles,
      documentFiles,
    };
  }, [files]);

  // const fileUrl = (file?: File) => {
  //   if (file) {
  //     return URL.createObjectURL(file);
  //   }

  //   return '';
  // };

  const isSavingDisabled = useMemo(() => {
    return !text?.trim() && files?.length === 0;
  }, [text, files]);

  // Preload emoji picker chunk so it's ready when user clicks the smiley button
  useEffect(() => {
    import('../EmojiPickerModel/EmojiPickerModel');
  }, []);

  const openEmojiSheet = () => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    emojiCursorRef.current = { start, end };
    setIsEmojiSheetOpen(true);
  };

  const prefetchEmojiPicker = () => {
    import('../EmojiPickerModel/EmojiPickerModel');
  };

  const handleEmojiSelectFromSheet = (emoji: string) => {
    const { start, end } = emojiCursorRef.current;
    setText((prev) => prev.slice(0, start) + emoji + prev.slice(end));
    const newCursor = start + emoji.length;
    setTimeout(() => {
      inputRef.current?.setSelectionRange(newCursor, newCursor);
      inputRef.current?.focus();
    }, 10);
    setIsEmojiSheetOpen(false);
  };

  const removeFile = (f: File) => {
    const fileIndex = files.findIndex((file) => file.file.name === f.name);

    if (fileIndex !== -1) {
      setFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    }
  };

  const onReset = () => {
    setText('');
    setMentionsMap(new Map());
    setFiles([]);
    setEmptySearchResults([]);
  };

  const replaceAllMentions = (text: string) => {
    let replacedText = text;

    mentionsMap.forEach((value, key) => {
      const regex = new RegExp(key, 'g');
      replacedText = replacedText.replace(regex, value);
    });

    return replacedText;
  };

  // add to mentions map in the below format
  // key: `@${userName}`
  // value: `{{${userId}#${userName}}}`
  const addToMentionsMap = (id: string, display: string) => {
    setMentionsMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(`@${display}`, `{{${id}#${display}}}`);
      return newMap;
    });
  };

  const handleSend = () => {
    const textValue = text.trim();

    // if (!textValue) return;

    if (!textValue && files?.length === 0) {
      message.warning('Type a message or upload a file to send');
      return;
    }

    onSend(
      replaceAllMentions(textValue) || ' ', // add a space to avoid sending empty message when only files are being sent
      files.map((f) => f.file),
      onReset,
    );
  };

  const onMentionSearch = async (
    searchText: string,
    callback: (mentions: IMentionedUser[]) => void,
  ) => {
    if (!searchMentionedUser) {
      callback([]);
      return;
    }

    if (searchText.length === 0) {
      callback([]);
      return;
    }

    if (emptySearchResults.find((item) => searchText.startsWith(item))) {
      callback([]);
      // eslint-disable-next-line no-useless-return
      return;
    }

    try {
      const result = await searchMentionedUser(searchText);

      if (result.length === 0) {
        setEmptySearchResults((prev) => [...prev, searchText]);
      }

      callback(
        result.map((user) => ({
          id: user.id,
          display: user.display,
          profilePicUrl: user.profilePicUrl,
        })),
      );
    } catch (error) {
      showAppError(error);
    }
  };

  // const scrollToBottomOfFilesContainer = () => {
  //   if (filesContainerRef.current) {
  //     filesContainerRef.current.scrollTop =
  //       filesContainerRef.current.scrollHeight;
  //   }
  // };

  useEffect(() => {
    if (!messageText) return;

    const { text: plainText, mentionsMap: usersMentionsMap } =
      extractTextAndMentionsMap(messageText);

    setText(plainText);
    setMentionsMap(usersMentionsMap);
  }, [messageText]);

  useEffect(() => {
    if (!inputRef.current || !suggestionContainerRef.current) return;

    const inputRect = inputRef.current?.getBoundingClientRect();
    const dropdownHeight = 350; // estimated or fixed max height
    const containerRect = containerElement?.getBoundingClientRect();

    if (inputRect) {
      const spaceAbove = inputRect.top - (containerRect?.top ?? 0);
      const spaceBelow =
        (containerRect?.bottom ?? window.innerHeight) - inputRect.bottom;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setMentionsDropdownPosition('top');
      } else {
        setMentionsDropdownPosition('bottom');
      }
    }
  }, [containerElement, text]);

  const handleSelectMedia = (media: ChatMediaType) => {
    if (media === 'poll') {
      setText('');
      setFiles([]);
      setEmptySearchResults([]);
      setIsFilesDropdownOpen(false);
      onAddPoll?.(text);
      return;
    }

    setIsFilesDropdownOpen(false);
    uploadFilesHandler({
      type: media as SingleFileType,
      mode: 'multiple',
      onUpload: (items, type) => {
        uploadFiles({
          files: items,
          type,
          maxSizeUnit: 'MB',
          maxSize: COMMUNITY_MAX_INPUT_MEDIA_FILE_SIZE,
          uploadCallback: (list) => {
            const newFiles = [
              ...files,
              ...list.map((f) => ({
                type: getChatFileType(media, f),
                file: f,
                contentUrl: URL.createObjectURL(f),
              })),
            ];

            if (newFiles.length > MAX_FILES_LIMIT) {
              message.warning(
                `You can only upload up to ${MAX_FILES_LIMIT} files at once`,
              );
            }

            setFiles(newFiles.slice(0, MAX_FILES_LIMIT));
          },
        });
      },
    });
  };

  if (hidden) return null;

  return (
    <>
      <div
        ref={filesContainerRef}
        className="chat-input__files slimScroll transparent"
        style={{ display: files.length === 0 ? 'none' : undefined }}>
        {(['videoFiles', 'imageFiles', 'audioFiles', 'documentFiles'] as const)
          .map((key) => state[key])
          .filter((files) => files.length > 0) // Keep only non-empty file groups
          .map((files, index, filteredFiles) => (
            <>
              {files.map((f) => (
                <ChatInputFilePreview
                  key={f.file.name}
                  type={f.type}
                  file={f.file}
                  contentUrl={f.contentUrl}
                  onRemove={() => removeFile(f.file)}
                />
              ))}
              {index < filteredFiles.length - 1 && (
                <Divider type="vertical" style={filesDividerStyle} />
              )}
            </>
          ))}
      </div>
      <div
        className="chat-input__wrapper"
        style={{ paddingBottom: isFooterActionsVisible ? '40px' : 0 }}>
        <div
          className="chat-input__mentions slimScroll transparent"
          ref={suggestionContainerRef}
          style={{
            top: mentionsDropdownPosition === 'bottom' ? '100%' : 'auto',
            bottom: mentionsDropdownPosition === 'top' ? '100%' : 'auto',
          }}
        />

        {hideUpload || accept.length === 0 ? null : (
          <>
            <span
              className="chat-input__add-btn__wrapper"
              role="button"
              tabIndex={0}
              onClick={() => !isSending && setIsFilesDropdownOpen(true)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') &&
                !isSending &&
                setIsFilesDropdownOpen(true)
              }
              style={
                isSending ? { cursor: 'not-allowed', opacity: 0.5 } : undefined
              }>
              <TMIcon
                name="add"
                size={32}
                color={COLORS.ICON}
                className="chat-input__add-btn"
                style={{
                  flexShrink: 0,
                  alignSelf: 'center',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                }}
              />
            </span>
            <BottomSheet
              open={isFilesDropdownOpen}
              onDismiss={() => setIsFilesDropdownOpen(false)}>
              {accept.map((media) => (
                <BottomSheetTile
                  key={media}
                  title={MEDIA_CONTENT[media].title}
                  icon={
                    <TMIcon
                      name={MEDIA_CONTENT[media].icon}
                      size={20}
                      color={MEDIA_CONTENT[media].color}
                    />
                  }
                  onClick={() => handleSelectMedia(media)}
                />
              ))}
            </BottomSheet>
          </>
        )}

        <div className="chat-input__box__wrapper">
          <MentionsInput
            ref={mentionsRef}
            inputRef={inputRef}
            // id={`input${message?._id ?? ''}`}
            className="chat-input__box"
            disabled={isSending}
            placeholder={placeholder}
            singleLine={false}
            autoFocus
            allowSpaceInQuery
            suggestionsPortalHost={suggestionContainerRef.current ?? undefined}
            // allowSuggestionsAboveCursor
            // forceSuggestionsAboveCursor
            value={text}
            onFocus={prefetchEmojiPicker}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(_: any, __: string, value: string) => {
              // add plain text value only,
              // so that we can replace the mentions using mentions map at the time of sending
              setText(value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            style={{ width: '100%' }}>
            <Mention
              trigger="@"
              data={onMentionSearch}
              appendSpaceOnAdd
              markup="{{__id__#__display__}}"
              displayTransform={(_, display) => `@${display}`}
              onAdd={(id, display) => addToMentionsMap(id as string, display)}
              renderSuggestion={(
                suggestion: SuggestionDataItem,
                _: string,
                highlightedDisplay: React.ReactNode,
              ) => (
                <div className="mention-suggestion">
                  <Space size={8}>
                    <Avatar
                      src={(suggestion as IMentionedUser).profilePicUrl}
                      alt="image"
                      size={32}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography.Text className="mention-suggestion__name">
                      {highlightedDisplay}
                    </Typography.Text>
                  </Space>
                </div>
              )}
            />
          </MentionsInput>

          <span
            className="chat-input__emoji-btn__wrapper"
            role="button"
            tabIndex={0}
            onClick={() => !isSending && openEmojiSheet()}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') &&
              !isSending &&
              openEmojiSheet()
            }
            onMouseEnter={prefetchEmojiPicker}
            onFocus={prefetchEmojiPicker}
            style={
              isSending ? { cursor: 'not-allowed', opacity: 0.5 } : undefined
            }
            aria-label="Add emoji">
            <BsEmojiSmile
              size={ICON_SIZE}
              color={COLORS.ICON}
              className="chat-input__box__emoji-btn"
              style={{ cursor: isSending ? 'not-allowed' : 'pointer' }}
            />
          </span>
        </div>

        {isEmojiSheetOpen && (
          <Suspense
            fallback={
              <BottomSheet
                open
                onDismiss={() => setIsEmojiSheetOpen(false)}
                className="select__bottomSheet">
                <div className="emoji_container">
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 320,
                      padding: 24,
                      gap: 16,
                    }}>
                    <Spin size="large" />
                    <Typography.Text type="secondary">Loading…</Typography.Text>
                  </div>
                </div>
              </BottomSheet>
            }>
            <EmojiPickerModel
              show={isEmojiSheetOpen}
              closeModal={() => setIsEmojiSheetOpen(false)}
              onSelectEmoji={handleEmojiSelectFromSheet}
            />
          </Suspense>
        )}

        <div className="chat-input__actions">
          {isSending ? (
            <LoadingOutlined
              style={{
                flexShrink: 0,
                fontSize: ICON_SIZE,
                color: COLORS.PRIMARY,
              }}
            />
          ) : (
            <TMIcon
              name="send"
              size={ICON_SIZE}
              color={isSavingDisabled ? COLORS.TEXT3 : COLORS.PRIMARY}
              className="chat-input__actions__icon-btn"
              // onClick={handleSend}
              onClick={isSavingDisabled ? undefined : handleSend}
              style={
                isSavingDisabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}
              }
            />
          )}
        </div>
      </div>

      {/* {files.length > 0 ? ( */}

      {/* ) : null} */}
    </>
  );
};

export default memo(ChatInputBox);
